import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { useTranslation } from "react-i18next";
import { useSettings } from '../../../../context/settings';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/sales/recurring-invoices/edit/${data.id}`, { state: { invoiceData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setInvoiceToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function RecurringInvoicesPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  const getRecurringInvoices = useCallback(async () => {
    setLoading(true);
    const data = await get('/sales/recurring-invoices?page=' + currentPage + '&page_size=' + pageSize);
    if (data?.invoices?.data) {
      setRowData(data.invoices.data);
      setTotalPages(data.invoices.last_page);
      setTotalRecords(data.invoices.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getRecurringInvoices();
  }, [getRecurringInvoices]);

  const deleteInvoice = async () => {
    const data = await del('/sales/recurring-invoices/delete/' + invoiceToDelete);

    if (data?.success) {
      toast.success(data.message || t('sales.invoiceDeleted'));
      getRecurringInvoices();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setInvoiceToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'invoice_number', headerName: '#', width: 80, sortable: true },
    {
      field: 'customer',
      headerName: t('crm.customer'),
      width: 240,
      sortable: true,
      valueFormatter: (params) => {
        const companyName = params.data?.customer?.company_name;
        if (companyName) return companyName;
        const firstName = params.data?.customer?.firstname || '';
        const lastName = params.data?.customer?.lastname || '';
        return `${firstName} ${lastName}`.trim() || t('common.notAvailable');
      }
    },
    {
      field: 'total_amount',
      headerName: t('common.amount'),
      width: 150,
      sortable: true,
      valueFormatter: (params) => {
        return params?.data?.total_amount + ' ' + (params?.data?.currency || '');
      }
    },
    { field: 'start_date', headerName: t('sales.startDate'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    { field: 'end_date', headerName: t('sales.endDate'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    { field: 'frequency', headerName: t('sales.frequency'), width: 140, sortable: true },
    {
      headerName: t('common.options'),
      flex: 1,
      minWidth: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setInvoiceToDelete }), [navigate]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sales.recurringInvoices')} onReload={getRecurringInvoices} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/sales/recurring-invoices/create')}>
            {t('sales.newRecurring')}
          </DefaultButton>
        </PageSubHeader>
        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={invoiceToDelete !== null}
        title={t('sales.deleteRecurring')}
        message={t('sales.deleteRecurringConfirm')}
        onConfirm={deleteInvoice}
        toggle={() => setInvoiceToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default RecurringInvoicesPage;
